import React, { useState, useEffect } from 'react';
import { formatearMoneda } from '../utils/moneda';
import Swal from 'sweetalert2';
import { ESTADOS_ANTEOJO, actualizarEstadoAnteojo, editarAnteojo, obtenerCristalesPorTipo, obtenerPreviewWhatsApp, obtenerPreviewReview, descargarFacturaPDF } from '../api/backend_v2';
import { mostrarModalReview } from '../utils/reviewHelper';
import { construirUrlWhatsApp, formatearTelefonoWhatsApp } from '../utils/whatsappHelper';
import { ArrowPathIcon, PencilIcon, XCircleIcon, EyeIcon, PrinterIcon, ChevronDownIcon, ChevronRightIcon, CheckIcon, ShieldCheckIcon, DocumentTextIcon, DocumentArrowDownIcon } from '@heroicons/react/24/outline';
import { solicitarFacturacionAnteojo, encontrarFacturaDeAnteojo, verODescargarFactura } from '../utils/facturacionBridge';
import PrintSobre from './PrintSobre';
import ModalGestionGarantia from '../components/ModalGestionGarantia';

const AnteojoDetalle = ({ anteojo, cliente, facturasCliente = [], onClose, onEstadoChange, onSave, onAplicarBeneficioYReplicar, onViewChange }) => {
  const [formData, setFormData] = useState({ ...anteojo });
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [mostrarLejos, setMostrarLejos] = useState(false);
  const [mostrarCerca, setMostrarCerca] = useState(false);
  const [mostrarOtrasMedidas, setMostrarOtrasMedidas] = useState(false);
  const [permiteSeguro, setPermiteSeguro] = useState(false);
  const [permitePromocion, setPermitePromocion] = useState(false);
  const [mostrarPrint, setMostrarPrint] = useState(false);
  const [mostrarModalGarantia, setMostrarModalGarantia] = useState(false);
  const esAnteojoCreadoPorBeneficio = (() => {
    const texto = String(formData?.observaciones || '').toLowerCase();
    return texto.includes('lente por seguro') || texto.includes('lente por promocion');
  })();

  // Graduaciones lejos/cerca
  const lejosFields = [
    { label: 'Esf', name: 'lejos_esf_od', ojo: 'OD' },
    { label: 'Cil', name: 'lejos_cil_od', ojo: 'OD' },
    { label: 'Eje', name: 'lejos_eje_od', ojo: 'OD' },
    { label: 'Esf', name: 'lejos_esf_oi', ojo: 'OI' },
    { label: 'Cil', name: 'lejos_cil_oi', ojo: 'OI' },
    { label: 'Eje', name: 'lejos_eje_oi', ojo: 'OI' },
  ];

  const cercaFields = [
    { label: 'Esf', name: 'cerca_esf_od', ojo: 'OD' },
    { label: 'Cil', name: 'cerca_cil_od', ojo: 'OD' },
    { label: 'Eje', name: 'cerca_eje_od', ojo: 'OD' },
    { label: 'Esf', name: 'cerca_esf_oi', ojo: 'OI' },
    { label: 'Cil', name: 'cerca_cil_oi', ojo: 'OI' },
    { label: 'Eje', name: 'cerca_eje_oi', ojo: 'OI' },
  ];

  // Otras medidas
  const otrasMedidasFields = [
    'altura_od', 'altura_oi', 'dnp_od', 'dnp_oi', 'grados_prisma_od', 'grados_prisma_oi', 'base_od', 'base_oi'
  ];

  const lejosFieldsOD = [
    { label: 'Esf', name: 'lejos_esf_od' },
    { label: 'Cil', name: 'lejos_cil_od' },
    { label: 'Eje', name: 'lejos_eje_od' },
  ];
  const lejosFieldsOI = [
    { label: 'Esf', name: 'lejos_esf_oi' },
    { label: 'Cil', name: 'lejos_cil_oi' },
    { label: 'Eje', name: 'lejos_eje_oi' },
  ];
  const cercaFieldsOD = [
    { label: 'Esf', name: 'cerca_esf_od' },
    { label: 'Cil', name: 'cerca_cil_od' },
    { label: 'Eje', name: 'cerca_eje_od' },
  ];
  const cercaFieldsOI = [
    { label: 'Esf', name: 'cerca_esf_oi' },
    { label: 'Cil', name: 'cerca_cil_oi' },
    { label: 'Eje', name: 'cerca_eje_oi' },
  ];

  useEffect(() => {
    setFormData({ ...anteojo });
    const tieneLejos = lejosFields.some(f => anteojo[f.name]);
    const tieneCerca = cercaFields.some(f => anteojo[f.name]);
    const tieneOtrasMedidas = otrasMedidasFields.some(f => anteojo[f]);
    setMostrarLejos(tieneLejos);
    setMostrarCerca(tieneCerca);
    setMostrarOtrasMedidas(tieneOtrasMedidas);
  }, [anteojo]);

  useEffect(() => {
    let activo = true;

    const normalizar = (valor) => String(valor || '').toLowerCase().trim().replace(/\s+/g, ' ');
    const extraerNombreProducto = (valor) => {
      const texto = String(valor || '').trim();
      if (!texto) return '';
      const idx = texto.indexOf('-');
      if (idx >= 0) {
        const nombre = texto.slice(idx + 1).trim();
        return nombre || texto;
      }
      return texto;
    };

    const cargarCapacidades = async () => {
      try {
        const nombresCristal = [formData?.lejos_cristal, formData?.cerca_cristal]
          .map(extraerNombreProducto)
          .filter(Boolean)
          .map(normalizar);

        if (!formData?.tipo_anteojo || nombresCristal.length === 0) {
          if (!activo) return;
          setPermiteSeguro(false);
          setPermitePromocion(false);
          return;
        }

        const response = await obtenerCristalesPorTipo(formData.tipo_anteojo);
        const cristales = response?.cristales || [];

        const coincidentes = cristales.filter((cristal) => {
          const nombreProductoNormalizado = normalizar(cristal.producto);
          const marcaNormalizada = normalizar(cristal.marca);
          
          return nombresCristal.some(nombreForm => 
            nombreForm.includes(nombreProductoNormalizado) && nombreForm.includes(marcaNormalizada)
          );
        });

        if (!activo) return;
        
        const tieneSeguro = coincidentes.some((c) => !!c.tiene_seguro);
        const tienePromocion = coincidentes.some((c) => !!c.tiene_promocion);

        setPermiteSeguro(tieneSeguro);
        setPermitePromocion(tienePromocion);
      } catch (err) {
        if (!activo) return;
        setPermiteSeguro(false);
        setPermitePromocion(false);
      }
    };

    cargarCapacidades();

    return () => {
      activo = false;
    };
  }, [formData?.tipo_anteojo, formData?.lejos_cristal, formData?.cerca_cristal]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({ ...formData, [name]: type === 'checkbox' ? checked : value });
  };

  const handleSave = async () => {
    const result = await Swal.fire({
      title: '¿Esta seguro de editar los datos del anteojo?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Si, guardar',
      cancelButtonText: 'Cancelar',
      reverseButtons: true
    });
    if (!result.isConfirmed) return;
    setIsSaving(true);
    try {
      await onSave(formData);
      setIsEditing(false);
      await Swal.fire({
        title: 'Datos editados correctamente',
        icon: 'success',
        timer: 1800,
        showConfirmButton: false
      });
    } catch (err) {
      setError(err.message);
      await Swal.fire({
        title: 'Error al editar',
        text: err.message || String(err),
        icon: 'error',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleEstadoChange = async (nuevoEstado) => {
    const result = await Swal.fire({
      title: `¿Cambiar estado a "${nuevoEstado}"?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Si, cambiar',
      cancelButtonText: 'Cancelar',
      reverseButtons: true
    });
    if (!result.isConfirmed) return;
    try {
      const respuesta = await actualizarEstadoAnteojo(formData.id, nuevoEstado);
      setFormData(prev => ({ ...prev, estado: nuevoEstado, saldo: respuesta?.ingreso_registrado ? 0 : prev.saldo }));

      if (nuevoEstado === 'Entregado') {
        try {
          const preview = await obtenerPreviewReview(formData.id);
          await mostrarModalReview({
            preview,
            Swal,
            formatearTelefonoWALista: formatearTelefonoWhatsApp,
            onReviewRegistrada: () => {
              if (onEstadoChange) onEstadoChange(formData.id, nuevoEstado);
            },
          });
        } catch (err) {
          console.error('Error al obtener preview de review:', err);
          await Swal.fire({
            title: 'Anteojo entregado',
            html: respuesta?.ingreso_registrado
              ? `<p class="text-gray-700">El saldo pendiente de <strong>${formatearMoneda(respuesta.saldo_cobrado)}</strong> fue registrado como ingreso correctamente.</p>`
              : `<p class="text-gray-700">El anteojo fue marcado como entregado correctamente.</p>`,
            icon: 'success',
            confirmButtonText: 'Aceptar',
          });
        }
      } else if (nuevoEstado === 'Para retirar') {
        try {
          const preview = await obtenerPreviewWhatsApp(formData.id);
          const urlWA = construirUrlWhatsApp(preview.telefono, preview.mensaje);
          const infoDestinatario = preview.hay_tutor
            ? `<strong>${preview.destinatario_nombre}</strong> (tutor/a de <em>${preview.cliente_nombre}</em>)`
            : `<strong>${preview.destinatario_nombre}</strong>`;

          await Swal.fire({
            title: '📦 Listo para retirar',
            html: `
              <p class="text-sm text-gray-600 mb-3">¿Querés notificar a ${infoDestinatario} por WhatsApp?</p>
              <div class="bg-gray-50 border rounded p-3 text-left text-sm whitespace-pre-wrap text-gray-800 mb-4" style="max-height:200px;overflow-y:auto">${preview.mensaje}</div>
              <a href="${urlWA}" target="_blank" rel="noopener noreferrer"
                class="inline-flex items-center gap-2 px-5 py-2 rounded-lg bg-green-500 text-white font-semibold hover:bg-green-600">
                💬 Abrir WhatsApp
              </a>`,
            showConfirmButton: false,
            showCloseButton: true,
          });
        } catch {
          // preview no crítico
        }
      } else {
        await Swal.fire({
          title: 'Estado actualizado',
          icon: 'success',
          timer: 1200,
          showConfirmButton: false
        });
      }

      // Notificar al padre DESPUÉS de los Swals para evitar re-renders que los interrumpan
      if (onEstadoChange) onEstadoChange(formData.id, nuevoEstado);
    } catch (err) {
      setError(err.message);
      await Swal.fire({
        title: 'Error al cambiar estado',
        text: err.message || String(err),
        icon: 'error',
      });
    }
  };

  const activarBeneficio = async (campo, etiqueta) => {
    const result = await Swal.fire({
      title: `¿Aplicar ${etiqueta}?`,
      text: 'Se guardara en este anteojo y se abrira la carga de uno nuevo con la misma graduacion.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Si, continuar',
      cancelButtonText: 'Cancelar',
      reverseButtons: true,
    });

    if (!result.isConfirmed) return;

    try {
      setIsSaving(true);
      await editarAnteojo(formData.id, { [campo]: true });
      const actualizado = { ...formData, [campo]: true };
      setFormData(actualizado);

      if (onAplicarBeneficioYReplicar) {
        onAplicarBeneficioYReplicar(actualizado, campo);
      }
    } catch (err) {
      setError(err.message);
      await Swal.fire({
        title: `Error al aplicar ${etiqueta.toLowerCase()}`,
        text: err.message || String(err),
        icon: 'error',
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Solo mostrar campos con informacion
  const campos = [
    { label: 'Tipo de Anteojo', name: 'tipo_anteojo' },
    { label: 'Nro. Sobre', name: 'nro_sobre' },
    { label: 'Cristal Lejos', name: 'lejos_cristal' },
    { label: 'Cristal Cerca', name: 'cerca_cristal' },
    { label: 'Armazon Lejos', name: 'lejos_armazon' },
    { label: 'Armazon Cerca', name: 'cerca_armazon' },
    { label: 'Precio Total', name: 'precio_total', format: (v) => formatearMoneda(v) },
    { label: 'Sena', name: 'sena', format: (v) => formatearMoneda(v) },
    { label: 'Saldo', name: 'saldo', format: (v) => formatearMoneda(v) },
    { label: 'Estado', name: 'estado' },
    { label: 'Forma de Pago', name: 'tipo_pago', format: (v) => ({ efectivo: 'Efectivo', tarjeta_debito: 'Tarjeta Débito', tarjeta_credito: 'Tarjeta Crédito', transferencia: 'Transferencia' })[v] || v },
    { label: 'Usa Seguro', name: 'usa_seguro', tipo: 'checkbox' },
    { label: 'Usa Promocion', name: 'usa_promocion', tipo: 'checkbox' },
    { label: 'Observaciones', name: 'observaciones' },
  ];

  const estadosOrden = ['Tomado', 'Preparado', 'En laboratorio', 'Para retirar', 'Entregado'];
  const estadoActualIdx = estadosOrden.indexOf(formData.estado);
  const estadosDisponibles = estadoActualIdx >= 0 ? estadosOrden.slice(estadoActualIdx + 1) : [];

  return (
    <>
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-3 sm:p-5 border w-full sm:w-11/12 xl:w-5/6 shadow-lg rounded-md bg-white mb-4">
        <div className="flex items-center justify-between pb-4 border-b">
          <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
            <EyeIcon className="h-6 w-6" /> Detalle del Anteojo
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XCircleIcon className="h-6 w-6" />
          </button>
        </div>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-2 rounded my-2">
            <strong>Error:</strong> {error}
          </div>
        )}

        <div className="flex flex-wrap gap-2 mt-6">
            {!isEditing && (
              <button type="button" className="btn-primary flex items-center gap-2" onClick={() => setIsEditing(true)}>
                <PencilIcon className="h-5 w-5" />
                <span>Editar Anteojo</span>
              </button>
            )}
            {isEditing && (
              <button type="submit" className="btn-primary flex items-center gap-2" disabled={isSaving}>
                <CheckIcon className="h-5 w-5" />
                <span>{isSaving ? 'Guardando...' : 'Guardar Cambios'}</span>
              </button>
            )}
            {!isEditing && (
              <button
                type="button"
                className="btn-secondary flex items-center gap-2"
                onClick={() => setMostrarPrint(true)}
              >
                <PrinterIcon className="h-5 w-5" />
                <span>Imprimir Sobre</span>
              </button>
            )}
            {!isEditing && (() => {
              const facturaAsociada = encontrarFacturaDeAnteojo(formData, facturasCliente);
              if (facturaAsociada) {
                return (
                  <button
                    type="button"
                    className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg flex items-center gap-2 transition font-medium"
                    onClick={() => verODescargarFactura(facturaAsociada, descargarFacturaPDF)}
                  >
                    <DocumentArrowDownIcon className="h-5 w-5" />
                    <span>Ver / Descargar Factura</span>
                  </button>
                );
              }
              if (Number(formData.saldo || 0) <= 0) {
                return (
                  <button
                    type="button"
                    className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg flex items-center gap-2 transition font-medium"
                    onClick={() => solicitarFacturacionAnteojo({ cliente, anteojo: formData, onViewChange, onCloseModal: onClose })}
                  >
                    <DocumentTextIcon className="h-5 w-5" />
                    <span>Realizar Factura</span>
                  </button>
                );
              }
              return null;
            })()}
            {!isEditing && (
              <button
                type="button"
                className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg flex items-center gap-2 transition"
                onClick={() => setMostrarModalGarantia(true)}
              >
                <ShieldCheckIcon className="h-5 w-5" />
                <span>Gestión de Garantía</span>
              </button>
            )}
            <button type="button" className="btn-secondary flex items-center gap-2" onClick={onClose}>
              <XCircleIcon className="h-5 w-5" />
              <span>Cerrar</span>
            </button>
          </div>

        {!isEditing && estadosDisponibles.length > 0 && (
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4 mt-4 mb-6">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <ArrowPathIcon className="h-5 w-5" />
                <h4 className="font-semibold text-blue-900">Cambiar Estado del Anteojo</h4>
              </div>
              <span className="text-sm text-blue-600 bg-blue-100 px-2 py-1 rounded-full">
                Estado actual: {formData.estado}
              </span>
            </div>
            <p className="text-sm text-blue-700 mb-3">
              Selecciona el nuevo estado para avanzar en el proceso:
            </p>
            <div className="flex flex-wrap gap-3">
              {estadosDisponibles.map((estado) => {
                const estadosColor = {
                  Tomado: 'bg-blue-500 hover:bg-blue-600 text-white',
                  Preparado: 'bg-yellow-500 hover:bg-yellow-600 text-white',
                  'En laboratorio': 'bg-purple-500 hover:bg-purple-600 text-white',
                  'Para retirar': 'bg-orange-500 hover:bg-orange-600 text-white',
                  Entregado: 'bg-green-500 hover:bg-green-600 text-white',
                  Anulado: 'bg-red-500 hover:bg-red-600 text-white',
                };
                return (
                  <button
                    key={estado}
                    type="button"
                    className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 shadow-sm flex items-center space-x-2 ${estadosColor[estado]}`}
                    onClick={() => handleEstadoChange(estado)}
                  >
                    <ChevronRightIcon className="h-4 w-4" />
                    <span>{estado}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {!isEditing && (
          <div className="bg-gradient-to-r from-emerald-50 to-lime-50 border border-emerald-200 rounded-lg p-4 mt-2 mb-6">
            <div className="flex flex-wrap items-center gap-3">
              {!esAnteojoCreadoPorBeneficio && permiteSeguro && !formData.usa_seguro && (
                <button
                  type="button"
                  className="px-4 py-2 rounded-lg font-medium bg-emerald-600 hover:bg-emerald-700 text-white transition-colors disabled:opacity-50"
                  onClick={() => activarBeneficio('usa_seguro', 'Seguro')}
                  disabled={isSaving}
                >
                  Usar Seguro
                </button>
              )}
              {!esAnteojoCreadoPorBeneficio && permitePromocion && !formData.usa_promocion && (
                <button
                  type="button"
                  className="px-4 py-2 rounded-lg font-medium bg-lime-600 hover:bg-lime-700 text-white transition-colors disabled:opacity-50"
                  onClick={() => activarBeneficio('usa_promocion', 'Promocion')}
                  disabled={isSaving}
                >
                  Usar Promocion
                </button>
              )}
              {!permiteSeguro && !permitePromocion && (
                <span className="text-sm text-emerald-800">Este cristal no tiene beneficios configurados.</span>
              )}
            </div>
          </div>
        )}

        <form className="space-y-8 mt-4" onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
          <div className="bg-gray-50 rounded-lg border border-gray-200 p-6 mb-6 shadow-sm">
            <h4 className="text-lg font-semibold text-gray-800 mb-4">Datos principales</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <div key="fecha_pedido" className="sm:col-span-1">
                <label className="block text-sm font-medium mb-1">Fecha del Pedido</label>
                <input
                  type="date"
                  name="fecha_pedido"
                  value={formData.fecha_pedido ? String(formData.fecha_pedido).slice(0, 10) : ''}
                  onChange={isEditing ? handleChange : undefined}
                  disabled={!isEditing}
                  className="input w-full"
                />
              </div>
              {campos.map((campo) => {
                const valor = formData[campo.name];
                if (campo.condicion && !campo.condicion()) return null;
                if (!isEditing && (campo.tipo === 'checkbox' ? valor === undefined : !valor && valor !== 0)) return null;
                
                const colSpan = campo.name === 'observaciones' ? 'sm:col-span-2 lg:col-span-3' : 'sm:col-span-1';
                
                if (campo.tipo === 'checkbox') {
                  return (
                    <div key={campo.name} className={colSpan}>
                      <label className="block text-sm font-medium mb-2 flex items-center gap-2">
                        <input
                          type="checkbox"
                          name={campo.name}
                          checked={!!valor}
                          onChange={isEditing ? handleChange : undefined}
                          disabled={!isEditing}
                          className="rounded"
                        />
                        {campo.label}
                      </label>
                    </div>
                  );
                }
                if (campo.name === 'estado') {
                  const estadosColor = {
                    Tomado: 'bg-blue-200 text-blue-800',
                    Preparado: 'bg-yellow-200 text-yellow-800',
                    'En laboratorio': 'bg-purple-200 text-purple-800',
                    'Para retirar': 'bg-orange-200 text-orange-800',
                    Entregado: 'bg-green-200 text-green-800',
                    Anulado: 'bg-red-200 text-red-800',
                  };
                  const estados = ['Tomado', 'Preparado', 'En laboratorio', 'Para retirar', 'Entregado', 'Anulado'];
                  return (
                    <div key={campo.name} className="sm:col-span-2 lg:col-span-3">
                      <label className="block text-sm font-medium mb-1">{campo.label}</label>
                      {isEditing ? (
                        <div className="flex flex-wrap gap-2">
                          {estados.map((estado) => (
                            <button
                              key={estado}
                              type="button"
                              className={`px-3 py-1 rounded font-semibold border transition-all ${estadosColor[estado]} ${formData.estado === estado ? 'border-2 border-black shadow' : 'border-gray-300'}`}
                              onClick={() => setFormData({ ...formData, estado })}
                            >
                              {estado}
                            </button>
                          ))}
                        </div>
                      ) : (
                        <span className={`inline-block px-3 py-1 rounded font-semibold ${estadosColor[valor]}`}>{valor}</span>
                      )}
                    </div>
                  );
                }
                if (campo.name === 'observaciones') {
                  return (
                    <div key={campo.name} className="sm:col-span-2 lg:col-span-3">
                      <label className="block text-sm font-medium">{campo.label}</label>
                      <textarea
                        name={campo.name}
                        value={valor}
                        onChange={isEditing ? handleChange : undefined}
                        disabled={!isEditing}
                        className="input w-full"
                        rows={2}
                      />
                    </div>
                  );
                }
                return (
                  <div key={campo.name} className={colSpan}>
                    <label className="block text-sm font-medium mb-1">{campo.label}</label>
                    <input
                      type="text"
                      name={campo.name}
                      value={campo.format && !isEditing ? campo.format(valor) : valor}
                      onChange={isEditing ? handleChange : undefined}
                      disabled={!isEditing}
                      className="input w-full"
                    />
                  </div>
                );
              })}
            </div>
          </div>

          {lejosFields.some(f => anteojo[f.name]) && (
            <div className="bg-gray-50 rounded-lg border border-gray-200 p-6 mb-6 shadow-sm">
              <div className="flex items-center gap-2 cursor-pointer mb-2" onClick={() => setMostrarLejos((v) => !v)}>
                {mostrarLejos ? <ChevronDownIcon className="h-4 w-4 text-gray-600" /> : <ChevronRightIcon className="h-4 w-4 text-gray-600" />}
                <h4 className="font-semibold text-base">Lejos</h4>
              </div>
              {mostrarLejos && (
                <div>
                  <div className="grid grid-cols-4 gap-2 text-base">
                    <div></div>
                    <div className="font-bold">Esf</div>
                    <div className="font-bold">Cil</div>
                    <div className="font-bold">Eje</div>
                  </div>
                  <div className="grid grid-cols-4 gap-2 text-base items-center mb-3">
                    <div className="font-bold">OD</div>
                    {lejosFieldsOD.map((f) => (
                      <div key={f.name} className="col-span-1">
                        <input
                          type="text"
                          name={f.name}
                          value={formData[f.name] || ''}
                          onChange={isEditing ? handleChange : undefined}
                          disabled={!isEditing}
                          className="input text-base px-2 py-1 w-full"
                        />
                      </div>
                    ))}
                  </div>
                  <div className="grid grid-cols-4 gap-2 text-base items-center mb-3">
                    <div className="font-bold">OI</div>
                    {lejosFieldsOI.map((f) => (
                      <div key={f.name} className="col-span-1">
                        <input
                          type="text"
                          name={f.name}
                          value={formData[f.name] || ''}
                          onChange={isEditing ? handleChange : undefined}
                          disabled={!isEditing}
                          className="input text-base px-2 py-1 w-full"
                        />
                      </div>
                    ))}
                  </div>
                  {['Multifocal', 'Bifocal', 'Ocupacional'].includes(formData.tipo_anteojo) && (
                    <div className="mt-4 pt-4 border-t border-gray-300">
                      <div className="flex flex-wrap gap-4">
                        <div>
                          <label className="block text-sm font-medium mb-2">Adición OD</label>
                          <input
                            type="number"
                            name="od_adicion"
                            value={formData.od_adicion || ''}
                            onChange={isEditing ? handleChange : undefined}
                            disabled={!isEditing}
                            className="input w-full sm:w-32"
                            step="0.25"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-2">Adición OI</label>
                          <input
                            type="number"
                            name="oi_adicion"
                            value={formData.oi_adicion || ''}
                            onChange={isEditing ? handleChange : undefined}
                            disabled={!isEditing}
                            className="input w-full sm:w-32"
                            step="0.25"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {cercaFields.some(f => anteojo[f.name]) && (
            <div className="bg-gray-50 rounded-lg border border-gray-200 p-6 mb-6 shadow-sm">
              <div className="flex items-center gap-2 cursor-pointer mb-2" onClick={() => setMostrarCerca((v) => !v)}>
                {mostrarCerca ? <ChevronDownIcon className="h-4 w-4 text-gray-600" /> : <ChevronRightIcon className="h-4 w-4 text-gray-600" />}
                <h4 className="font-semibold text-base">Cerca</h4>
              </div>
              {mostrarCerca && (
                <div>
                  <div className="grid grid-cols-4 gap-2 text-base">
                    <div></div>
                    <div className="font-bold">Esf</div>
                    <div className="font-bold">Cil</div>
                    <div className="font-bold">Eje</div>
                  </div>
                  <div className="grid grid-cols-4 gap-2 text-base items-center mb-3">
                    <div className="font-bold">OD</div>
                    {cercaFieldsOD.map((f) => (
                      <div key={f.name} className="col-span-1">
                        <input
                          type="text"
                          name={f.name}
                          value={formData[f.name] || ''}
                          onChange={isEditing ? handleChange : undefined}
                          disabled={!isEditing}
                          className="input text-base px-2 py-1 w-full"
                        />
                      </div>
                    ))}
                  </div>
                  <div className="grid grid-cols-4 gap-2 text-base items-center mb-3">
                    <div className="font-bold">OI</div>
                    {cercaFieldsOI.map((f) => (
                      <div key={f.name} className="col-span-1">
                        <input
                          type="text"
                          name={f.name}
                          value={formData[f.name] || ''}
                          onChange={isEditing ? handleChange : undefined}
                          disabled={!isEditing}
                          className="input text-base px-2 py-1 w-full"
                        />
                      </div>
                    ))}
                </div>
              </div>
              )}
            </div>
          )}

          {otrasMedidasFields.some(f => anteojo[f]) && (
            <div className="bg-gray-50 rounded-lg border border-gray-200 p-6 mb-6 shadow-sm">
              <div className="flex items-center gap-2 cursor-pointer mb-4" onClick={() => setMostrarOtrasMedidas((v) => !v)}>
                {mostrarOtrasMedidas ? <ChevronDownIcon className="h-4 w-4 text-gray-600" /> : <ChevronRightIcon className="h-4 w-4 text-gray-600" />}
                <h4 className="font-semibold text-base">Otras Medidas</h4>
              </div>
              
              {mostrarOtrasMedidas && (
              <div className="space-y-4">
                {/* Altura de montaje */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Altura OD (mm)</label>
                    <input
                      type="text"
                      name="altura_od"
                      value={formData.altura_od || ''}
                      onChange={isEditing ? handleChange : undefined}
                      disabled={!isEditing}
                      className="input w-full text-sm px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Altura OI (mm)</label>
                    <input
                      type="text"
                      name="altura_oi"
                      value={formData.altura_oi || ''}
                      onChange={isEditing ? handleChange : undefined}
                      disabled={!isEditing}
                      className="input w-full text-sm px-3 py-2"
                    />
                  </div>
                </div>

                {/* Distancia Pupilar */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">DNP OD (mm)</label>
                    <input
                      type="text"
                      name="dnp_od"
                      value={formData.dnp_od || ''}
                      onChange={isEditing ? handleChange : undefined}
                      disabled={!isEditing}
                      className="input w-full text-sm px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">DNP OI (mm)</label>
                    <input
                      type="text"
                      name="dnp_oi"
                      value={formData.dnp_oi || ''}
                      onChange={isEditing ? handleChange : undefined}
                      disabled={!isEditing}
                      className="input w-full text-sm px-3 py-2"
                    />
                  </div>
                </div>

                {/* Prisma */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Grados Prisma OD</label>
                    <input
                      type="text"
                      name="grados_prisma_od"
                      value={formData.grados_prisma_od || ''}
                      onChange={isEditing ? handleChange : undefined}
                      disabled={!isEditing}
                      className="input w-full text-sm px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Grados Prisma OI</label>
                    <input
                      type="text"
                      name="grados_prisma_oi"
                      value={formData.grados_prisma_oi || ''}
                      onChange={isEditing ? handleChange : undefined}
                      disabled={!isEditing}
                      className="input w-full text-sm px-3 py-2"
                    />
                  </div>
                </div>

                {/* Base */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Base OD</label>
                    <input
                      type="text"
                      name="base_od"
                      value={formData.base_od || ''}
                      onChange={isEditing ? handleChange : undefined}
                      disabled={!isEditing}
                      className="input w-full text-sm px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Base OI</label>
                    <input
                      type="text"
                      name="base_oi"
                      value={formData.base_oi || ''}
                      onChange={isEditing ? handleChange : undefined}
                      disabled={!isEditing}
                      className="input w-full text-sm px-3 py-2"
                    />
                  </div>
                </div>

              </div>
              )}
            </div>
          )}
          <div className="flex flex-wrap gap-2 mt-6">
            {!isEditing && (
              <button type="button" className="btn-primary flex items-center gap-2" onClick={() => setIsEditing(true)}>
                <PencilIcon className="h-5 w-5" />
                <span>Editar Anteojo</span>
              </button>
            )}
            {isEditing && (
              <button type="submit" className="btn-primary flex items-center gap-2" disabled={isSaving}>
                <CheckIcon className="h-5 w-5" />
                <span>{isSaving ? 'Guardando...' : 'Guardar Cambios'}</span>
              </button>
            )}
            <button type="button" className="btn-secondary flex items-center gap-2" onClick={onClose}>
              <XCircleIcon className="h-5 w-5" />
              <span>Cerrar</span>
            </button>
          </div>
        </form>
      </div>
    </div>

    {mostrarPrint && (
      <PrintSobre
        anteojo={formData}
        cliente={cliente}
        onClose={() => setMostrarPrint(false)}
      />
    )}

    <ModalGestionGarantia
      anteojo={formData}
      cliente={cliente}
      isOpen={mostrarModalGarantia}
      onClose={() => setMostrarModalGarantia(false)}
      onGarantiaCreada={() => {
        // Opcional: actualizar estado del cliente si es necesario
      }}
    />
    </>
  );
};

export default AnteojoDetalle;
